/**
 * Centralized Token Management Utility
 *
 * Handles ALL token operations (accessToken, publicToken, refreshToken)
 * using existing sessionStorage helpers with expiry management.
 *
 * CRITICAL: All auth flows MUST use this utility for token storage/retrieval
 * to ensure consistent lifecycle and expiry handling.
 */

import { jwtDecode } from 'jwt-decode';
import { SESSION_STORAGE_KEY, TOKEN_TYPES } from '../constant/auth.constant';
import { setSessionItemWithExpiry, getSessionItemWithExpiry } from './utility';
import { APP_CONFIG } from './config';
import { convertMinuteToMiliseconds } from './helpers';

/**
 * Token types managed by this utility
 */
export type TokenType = 'accessToken' | 'publicToken' | 'refreshToken' | 'tempToken';

/**
 * Organization review status enum (matches backend Prisma enum)
 */
type OrganizationReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED';

/**
 * Decoded JWT token interface
 * Matches backend IJwtPayload from src/modules/auth/interface/index.ts
 */
interface DecodedToken {
  // User identification
  sub: string;
  username: string;

  // Organization context
  organization_id: string | null;
  orgTimeZone: string;
  orgStatus: boolean;
  review: OrganizationReviewStatus | null;

  // Session & security
  session_id: string;
  accessTokenVersion: number;

  // Role & permissions
  role_name: string | null;
  privileges: Array<{ name: string }>;

  // Standard JWT claims
  exp?: number;
  iat?: number;
}

/**
 * Token configuration with expiry times (in minutes)
 */
const TOKEN_CONFIG: Record<TokenType, number> = {
  accessToken: (APP_CONFIG.ACCESS_TOKEN_EXPIRY as number) || 15, // 15 min default
  publicToken: 10, // 10 min default (hardcoded, not in APP_CONFIG)
  refreshToken: (APP_CONFIG.REFRESH_TOKEN_EXPIRY as number) || 1440, // 24 hours default
  tempToken: 5, // ~5 min — organization-selection temp token issued by /auth/login
};

/**
 * Storage key mapping for each token type
 */
const TOKEN_KEY_MAP: Record<TokenType, string> = {
  accessToken: SESSION_STORAGE_KEY.ACCESS_TOKEN,
  publicToken: SESSION_STORAGE_KEY.PUBLIC_TOKEN,
  refreshToken: SESSION_STORAGE_KEY.REFRESH_TOKEN,
  tempToken: SESSION_STORAGE_KEY.TEMP_TOKEN,
};

/**
 * Store a token with automatic expiry handling
 *
 * @param tokenType - Type of token (accessToken, publicToken, refreshToken)
 * @param token - Token value to store
 * @param customExpiryMinutes - Optional custom expiry time in minutes (overrides default)
 */
export const setToken = (
  tokenType: TokenType,
  token: string,
  customExpiryMinutes?: number,
): void => {
  const storageKey = TOKEN_KEY_MAP[tokenType];
  const expiryMinutes = customExpiryMinutes || TOKEN_CONFIG[tokenType];
  const expiryMs = convertMinuteToMiliseconds(expiryMinutes);

  try {
    setSessionItemWithExpiry(storageKey, token, expiryMs);
    console.debug(`[tokenManager] Token stored: ${tokenType} (expires in ${expiryMinutes} min)`);
  } catch (error) {
    console.error(`[tokenManager] Failed to set ${tokenType}:`, error);
  }
};

/**
 * Retrieve a token (returns null if expired)
 *
 * @param tokenType - Type of token to retrieve
 * @returns Token value or null if not found/expired
 */
export const getToken = (tokenType: TokenType): string | null => {
  const storageKey = TOKEN_KEY_MAP[tokenType];

  try {
    const token = getSessionItemWithExpiry(storageKey);
    if (token) {
      console.debug(`[tokenManager] Token retrieved: ${tokenType}`);
    }
    return token;
  } catch (error) {
    console.error(`[tokenManager] Failed to get ${tokenType}:`, error);
    return null;
  }
};

/**
 * Clear a specific token
 *
 * @param tokenType - Type of token to clear
 */
export const clearToken = (tokenType: TokenType): void => {
  const storageKey = TOKEN_KEY_MAP[tokenType];

  try {
    sessionStorage.removeItem(storageKey);
    console.debug(`[tokenManager] Token cleared: ${tokenType}`);
  } catch (error) {
    console.error(`[tokenManager] Failed to clear ${tokenType}:`, error);
  }
};

/**
 * Clear ALL tokens (used on logout)
 */
export const clearAllTokens = (): void => {
  try {
    clearToken(TOKEN_TYPES.ACCESS_TOKEN);
    clearToken(TOKEN_TYPES.PUBLIC_TOKEN);
    clearToken(TOKEN_TYPES.REFRESH_TOKEN);
    clearToken(TOKEN_TYPES.TEMP_TOKEN);
    console.debug('[tokenManager] All tokens cleared');
  } catch (error) {
    console.error('[tokenManager] Failed to clear all tokens:', error);
  }
};

/**
 * Check if a token exists and is not expired
 *
 * @param tokenType - Type of token to check
 * @returns true if token exists and is valid
 */
export const hasValidToken = (tokenType: TokenType): boolean => {
  return getToken(tokenType) !== null;
};

/**
 * Get all active tokens (debugging utility)
 *
 * @returns Object containing all currently valid tokens
 */
export const getAllTokens = (): Partial<Record<TokenType, string>> => {
  return {
    accessToken: getToken(TOKEN_TYPES.ACCESS_TOKEN) || undefined,
    publicToken: getToken(TOKEN_TYPES.PUBLIC_TOKEN) || undefined,
    refreshToken: getToken(TOKEN_TYPES.REFRESH_TOKEN) || undefined,
  };
};

/**
 * Batch token operations (set multiple tokens at once)
 * Used after login or token refresh
 *
 * @param tokens - Object with token values to set
 * @param customExpiry - Optional custom expiry times by token type
 */
export const setTokens = (
  tokens: Partial<Record<TokenType, string>>,
  customExpiry?: Partial<Record<TokenType, number>>,
): void => {
  try {
    if (tokens.accessToken) {
      setToken(TOKEN_TYPES.ACCESS_TOKEN, tokens.accessToken, customExpiry?.accessToken);
    }
    if (tokens.publicToken) {
      setToken(TOKEN_TYPES.PUBLIC_TOKEN, tokens.publicToken, customExpiry?.publicToken);
    }
    if (tokens.refreshToken) {
      setToken(TOKEN_TYPES.REFRESH_TOKEN, tokens.refreshToken, customExpiry?.refreshToken);
    }
    console.debug('[tokenManager] Batch tokens stored');
  } catch (error) {
    console.error('[tokenManager] Failed to set batch tokens:', error);
  }
};

/**
 * Decode the access token and return the decoded payload
 *
 * @returns Decoded token payload or null if token is invalid/not found
 */
export const getDecodedToken = (): DecodedToken | null => {
  const token = getToken(TOKEN_TYPES.ACCESS_TOKEN);
  if (!token) {
    console.debug('[tokenManager] No access token found for decoding');
    return null;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded;
  } catch (error) {
    console.error('[tokenManager] Failed to decode token:', error);
    return null;
  }
};

/**
 * Get organization ID from the access token
 *
 * @returns Organization ID string or null if not found/not set
 */
export const getOrganizationId = (): string | null => {
  const decoded = getDecodedToken();
  if (!decoded) return null;

  return decoded.organization_id || null;
};

/**
 * Get user ID from the access token (sub claim)
 *
 * @returns User ID string or null if not found
 */
export const getUserId = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.sub || null;
};

/**
 * Get user email from the access token (username field)
 *
 * @returns User email string or null if not found
 */
export const getUserEmail = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.username || null;
};

/**
 * Get organization timezone from the access token
 *
 * @returns Timezone string or null if not found
 */
export const getOrgTimeZone = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.orgTimeZone || null;
};

/**
 * Get organization active status from the access token
 *
 * @returns boolean indicating if organization is active, or null if not found
 */
export const getOrgStatus = (): boolean | null => {
  const decoded = getDecodedToken();
  return decoded?.orgStatus ?? null;
};

/**
 * Get organization review status from the access token
 *
 * @returns Review status or null if not found
 */
export const getOrgReviewStatus = (): OrganizationReviewStatus | null => {
  const decoded = getDecodedToken();
  return decoded?.review || null;
};

/**
 * Get current session ID from the access token
 *
 * @returns Session ID string or null if not found
 */
export const getSessionId = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.session_id || null;
};

/**
 * Get user's role name from the access token
 *
 * @returns Role name string or null if not found
 */
export const getRoleName = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.role_name || null;
};

/**
 * Get user's privileges from the access token
 *
 * @returns Array of privilege names or empty array if not found
 */
export const getPrivileges = (): string[] => {
  const decoded = getDecodedToken();
  return decoded?.privileges?.map((p) => p.name) || [];
};

/**
 * Check if user has a specific privilege
 *
 * @param privilegeName - The privilege name to check
 * @returns boolean indicating if user has the privilege
 */
export const hasPrivilege = (privilegeName: string): boolean => {
  const privileges = getPrivileges();
  return privileges.includes(privilegeName);
};

/**
 * Export tokenManager as a namespace-like object for consistency
 */
export const tokenManager = {
  // Token CRUD operations
  setToken,
  getToken,
  clearToken,
  clearAllTokens,
  hasValidToken,
  getAllTokens,
  setTokens,

  // Token decoding
  getDecodedToken,

  // User info
  getUserId,
  getUserEmail,

  // Organization info
  getOrganizationId,
  getOrgTimeZone,
  getOrgStatus,
  getOrgReviewStatus,

  // Session & security
  getSessionId,

  // Role & permissions
  getRoleName,
  getPrivileges,
  hasPrivilege,
};

/**
 * Alias for clearAllTokens (for convenience)
 */
export const clearTokens = clearAllTokens;

export default tokenManager;
